import apiClient from '../../../lib/axios';

export interface MessageReadByUser {
  id: number;
  name: string;
  pivot: { read_at: string };
}

export interface MessageReaction {
  wa_id: string;
  emoji: string;
  timestamp: string;
}

export interface MessageEditEntry {
  body: string;
  edited_at: string;
}

export interface Message {
  id: number;
  tenant_id: number;
  contact_id: number;
  wa_message_id: string | null;
  contact_wa_id: string | null;
  direction: 'inbound' | 'outbound';
  type: string;
  body: string | null;
  status: string;
  interactive_payload: Record<string, unknown> | null;
  raw_payload: Record<string, unknown> | null;
  sent_at: string;
  created_at: string;
  read_by?: MessageReadByUser[];
  media_url: string | null;
  media_mime_type: string | null;
  media_filename: string | null;
  media_size: number | null;
  context_wa_message_id: string | null;
  is_edited: boolean;
  edit_history: MessageEditEntry[] | null;
  is_revoked: boolean;
  reactions: MessageReaction[] | null;
  _localMediaUrl?: string;
  _uploadProgress?: number;
}

export interface CursorPaginatedResponse<T> {
  data: T[];
  next_cursor: string | null;
  prev_cursor: string | null;
  per_page: number;
}

export interface TemplateComponent {
  type: string;
  text?: string;
  format?: string;
  buttons?: { type: string; text: string; url?: string; phone_number?: string }[];
}

export interface MessageTemplate {
  id: string;
  name: string;
  status: string;
  language: string;
  category: string;
  components: TemplateComponent[];
}

export interface SendTemplateParameter {
  type: 'text' | 'image' | 'video' | 'document';
  text?: string;
  // Set for Meta v22+ named-placeholder templates (e.g. `{{merchant_name}}`).
  // Omitted for positional `{{1}}` / `{{2}}` templates.
  parameter_name?: string;
}

export interface SendTemplateComponent {
  type: 'header' | 'body' | 'button';
  parameters?: SendTemplateParameter[];
  sub_type?: string;
  index?: number;
}

// Client-rendered template chrome with `{{N}}` placeholders substituted.
// Persisted into `interactive_payload.rendered_components` so the chat
// bubble can reconstruct the full template (header / body / footer /
// buttons) without re-fetching the Meta template definition.
export type RenderedTemplateComponent =
  | { type: 'HEADER'; format: 'TEXT'; text: string }
  | { type: 'HEADER'; format: 'IMAGE' | 'VIDEO' | 'DOCUMENT' }
  | { type: 'BODY'; text: string }
  | { type: 'FOOTER'; text: string }
  | {
      type: 'BUTTONS';
      buttons: Array<{
        type: string;
        text: string;
        url?: string;
        phone_number?: string;
      }>;
    };

export interface SendMessagePayload {
  type?: 'text' | 'interactive' | 'template';
  body?: string;
  interactive?: Record<string, unknown>;
  template?: {
    name: string;
    language: string;
    components?: SendTemplateComponent[];
    rendered_components?: RenderedTemplateComponent[];
  };
  context_message_id?: string;
}

export const messagesApi = {
  list: (contactId: number, cursor?: string) =>
    apiClient.get<CursorPaginatedResponse<Message>>(`/v1/contacts/${contactId}/messages`, {
      params: cursor ? { cursor } : undefined,
    }),

  send: (contactId: number, data: SendMessagePayload) =>
    apiClient.post<Message>(`/v1/contacts/${contactId}/messages`, data),

  templates: () =>
    apiClient.get<{ data: MessageTemplate[] }>('/v1/templates'),

  unreadTotal: () =>
    apiClient.get<{ unread_total: number }>('/v1/contacts/unread-total'),

  markRead: (contactId: number) =>
    apiClient.post<{ unread_total: number }>(`/v1/contacts/${contactId}/mark-read`),

  sendMedia: (
    contactId: number,
    data: FormData,
    onUploadProgress?: (pct: number) => void,
  ) =>
    apiClient.post<Message>(`/v1/contacts/${contactId}/messages`, data, {
      // Media uploads can legitimately exceed the default 30s instance timeout on
      // slow links (large file + server-side WhatsApp upload); use a generous
      // per-request timeout so a real upload isn't aborted mid-flight. The
      // optimistic bubble still surfaces failure via onError if this is exceeded.
      timeout: 120_000,
      onUploadProgress: (e) => {
        if (onUploadProgress && e.total)
          onUploadProgress(Math.round((e.loaded / e.total) * 100));
      },
    }),

  sendReaction: (contactId: number, messageId: number, emoji: string) =>
    apiClient.post<Message>(
      `/v1/contacts/${contactId}/messages/${messageId}/react`,
      { emoji },
    ),

  downloadMedia: (contactId: number, messageId: number) =>
    apiClient.get<Blob>(`/v1/contacts/${contactId}/messages/${messageId}/media`, {
      responseType: 'blob',
    }),
};
